import { createFileRoute } from '@tanstack/react-router'
import { Crown, MonitorPlay, ShieldAlert, Trash2, Loader2, Image as ImageIcon } from 'lucide-react'
import { useTenantFeature } from '@/hooks/use-tenant-feature'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export const Route = createFileRoute('/app/tv-midia')({
  component: PublicidadeTelaComponent,
})

function PublicidadeTelaComponent() {
  const { hasAccess, isLoading, company } = useTenantFeature('publicidade_tela')
  const queryClient = useQueryClient()

  
  const companyId = company?.id

  const { data: ads = [], isLoading: isLoadingAds } = useQuery({
    queryKey: ['tenant_ads', companyId],
    queryFn: async () => {
      if (!companyId) return []
      const { data, error } = await supabase
        .from('tenant_publicidade_tela')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data
    },
    enabled: !!companyId && hasAccess,
  })
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fileExt = file.name.split('.').pop()
      const fileName = `${companyId}/${crypto.randomUUID()}.${fileExt}`
      const filePath = `${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('publicidade_media')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      const { data } = supabase.storage.from('publicidade_media').getPublicUrl(filePath)

      const { error: dbError } = await supabase
        .from('tenant_publicidade_tela')
        .insert({
          company_id: companyId,
          title: file.name,
          image_url: data.publicUrl,
          is_active: true
        })

      if (dbError) throw dbError
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant_ads', companyId] })
      toast.success('Mídia salva com sucesso!')
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    onError: (e: any) => toast.error(`Erro ao salvar mídia: ${e.message}`),
    onSettled: () => setUploading(false)
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string, is_active: boolean }) => {
      const { error } = await supabase.from('tenant_publicidade_tela').update({ is_active }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant_ads', companyId] })
      toast.success('Status atualizado')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tenant_publicidade_tela').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant_ads', companyId] })
      toast.success('Mídia removida')
    }
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      setSelectedFile(null)
      return
    }

    if (file.size > 15 * 1024 * 1024) {
      toast.error('O arquivo não pode ser maior que 15MB para garantir a performance da TV.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      setSelectedFile(null)
      return
    }

    setSelectedFile(file)
  }

  const handleUpload = () => {
    if (!selectedFile) return
    setUploading(true)
    uploadMutation.mutate(selectedFile)
  }

  if (isLoading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--brand-mid)]" />
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <ShieldAlert className="w-12 h-12 text-muted-foreground" />
        <h2 className="text-xl font-semibold">Página Não Encontrada</h2>
        <p className="text-muted-foreground">Esta página não existe ou você não tem acesso a ela.</p>
      </div>
    )
  }

  const activeCount = ads.filter((a: any) => a.is_active).length

  return (
    <div className="flex-1 space-y-4 p-8 pt-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Publicidade em Tela</h1>
        <p className="text-muted-foreground">Gerencie os anúncios exibidos no quadro do Kanban na TV do pátio.</p>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-200">
              Mídias Ativas
            </CardTitle>
            <MonitorPlay className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{activeCount}</div>
            <p className="text-xs text-slate-400">
              Transmitindo atualmente na TV
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-800 bg-slate-900/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-200">
            <Crown className="w-5 h-5 text-amber-400" />
            Adicionar Nova Mídia
          </CardTitle>
          <CardDescription className="text-slate-400">
            Faça upload de vídeos curtos (.mp4) ou imagens (.png, .jpg) de até 15MB.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex flex-col gap-2 w-full max-w-sm">
              <Label className="text-slate-300">Selecione o arquivo</Label>
              <div className="relative">
                <Input 
                  id="media" 
                  type="file" 
                  accept="video/mp4,image/png,image/jpeg"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="hidden"
                />
                <Label 
                  htmlFor="media" 
                  className="flex items-center gap-3 px-3 py-2 bg-slate-950 border border-slate-700 rounded-md cursor-pointer hover:bg-slate-900 transition-colors"
                >
                  <span className="bg-slate-800 text-slate-200 px-3 py-1.5 rounded text-sm font-medium shrink-0">
                    Escolher arquivo
                  </span>
                  <span className="text-slate-400 text-sm truncate">
                    {selectedFile ? selectedFile.name : 'Nenhum arquivo escolhido'}
                  </span>
                </Label>
              </div>
            </div>
            
            <Button 
              onClick={handleUpload} 
              disabled={!selectedFile || uploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[140px]"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Mídia'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4">Mídias Cadastradas</h3>
        {isLoadingAds ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-800 rounded-xl text-slate-500">
            <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>Nenhuma mídia cadastrada ainda.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {ads.map((ad: any) => {
              const isVideo = ad.image_url.toLowerCase().endsWith('.mp4')
              return (
                <Card key={ad.id} className={`overflow-hidden border-slate-800 transition-all ${ad.is_active ? 'bg-slate-900 ring-1 ring-amber-500/50' : 'bg-slate-900/50 opacity-60'}`}>
                  <div className="aspect-video bg-black relative flex items-center justify-center">
                    {isVideo ? (
                      <video src={ad.image_url} className="w-full h-full object-cover" controls={false} />
                    ) : (
                      <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" />
                    )}
                    {!ad.is_active && (
                      <div className="absolute inset-0 bg-slate-950/60 flex items-center justify-center backdrop-blur-sm">
                        <span className="bg-slate-900 text-slate-300 px-3 py-1 rounded-full text-xs font-medium border border-slate-700">Pausado</span>
                      </div>
                    )}
                  </div>
                  <CardHeader className="p-4">
                    <CardTitle className="text-sm truncate" title={ad.title}>{ad.title}</CardTitle>
                  </CardHeader>
                  <CardFooter className="p-4 pt-0 flex justify-between">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleMutation.mutate({ id: ad.id, is_active: !ad.is_active })}
                      className={ad.is_active ? 'border-amber-500/50 text-amber-500 hover:bg-amber-500/10' : ''}
                    >
                      {ad.is_active ? 'Desativar' : 'Ativar'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-950"
                      onClick={() => {
                        if (confirm('Remover esta mídia definitivamente?')) {
                          deleteMutation.mutate(ad.id)
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardFooter>
                </Card>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
